/**
 * transport/chatSessions.ts —— **会话面客户端**（入口侧会话管理：列表／新建）。
 *
 * 分工：`session.ts`＝**认证**会话（登录态 cookie）；本件＝**对话会话**（`cap_session.session`，一个会话承载一个任务）。
 * 纪律（与 `session.ts` 同）：路径经 `assertEgress` 白名单；**浏览器零凭据**（cookie 同源自动携带）；
 *   身份不自报（主体/标题由服务端回执给）；失败**显式抛**（禁静默假成功）。
 */
import { assertEgress, assertEgressWithin } from "./whitelist";
import type { FetchLike } from "./session";
import { E_CODES } from "./errorText";

/** 会话行（服务端 `session_store.to_entry` 回执形态） */
export interface ChatSession {
  id: string;
  title: string;
  state: string | null;
  activity_state: string | null;
  created_at: string | null;
}

export class ChatSessionError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(E_CODES.SESSION_FAILED + "：" + message);
    this.name = "ChatSessionError";
    this.status = status;
  }
}

async function call(init: RequestInit, fetchFn: FetchLike, what: string): Promise<unknown> {
  const path = assertEgress("sessions", "/api/sessions");
  const res = await fetchFn(path, { credentials: "same-origin", ...init });
  if (!res.ok) throw new ChatSessionError(res.status, what + "被拒（HTTP " + res.status + "）");
  return res.json();
}

/** 列表：按创建时间倒序（服务端排序；前端不再排一次——排序单源在后端） */
export async function listChatSessions(fetchFn: FetchLike = fetch): Promise<ChatSession[]> {
  const data = (await call({ method: "GET" }, fetchFn, "会话列表")) as ChatSession[];
  return Array.isArray(data) ? data : [];
}

/** 新建：标题可缺（缺 ⇒ 服务端给默认名，**前端不编造标题**） */
export async function createChatSession(fetchFn: FetchLike = fetch, title?: string): Promise<ChatSession> {
  const body = title && title.trim() ? { title: title.trim() } : {};
  return (await call({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }, fetchFn, "新建会话")) as ChatSession;
}

/**
 * ★L-PL-5c（PL5 前端三缺口）——**删会话**（服务端软删：`deleted=TRUE`，数据永存）。
 * 既有后端契约（**零新端点**）：`DELETE /api/sessions/{id}`
 * （`src/sononux/api/routes.py:100` `SESSION_ROUTES` ／ `:317 route_sessions_delete` →
 * `runtime/services/store/session/session_store.py:140` 软删）。路径经 `assertEgressWithin`
 * 白名单（`/api/sessions/<单段 id>` 既有形，同 `transport/models.ts::deleteModel` 范式）。
 * 失败**显式抛** `ChatSessionError`（禁静默假成功）；成功 ⇒ 回服务端回执（有的话，不编造）。
 */
export async function deleteChatSession(id: string, fetchFn: FetchLike = fetch): Promise<unknown> {
  const path = assertEgressWithin("sessions", "/api/sessions/" + encodeURIComponent(id));
  const res = await fetchFn(path, { method: "DELETE", credentials: "same-origin" });
  if (!res.ok) throw new ChatSessionError(res.status, "删除会话被拒（HTTP " + res.status + "）");
  // 204/空体 ⇒ 不硬要 JSON（不因回执形态编造失败）；有体 ⇒ 原样带回
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}
